import logo from '../../assets/owlbot_logo_login.png';
import Footer from '../../components/Footer/Footer';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FlashBar from '../../components/FlashBar/FlashBar';
import { flashBarTheme } from '../../config/flashBar';
import { colors } from '../../config/constants';
import { errorMessage } from '../../config/errorsMsg';
import axios from 'axios';
import { updateAutoLogout, updateTokenInfo } from '../../redux/action';
import ActivityIndicator from '../../components/ActivityIndicator/ActivityIndicator';
import { StateProps } from '../../redux/reducer';

type LoginRequestProps = {
  username: string;
  password: string;
};

type LoginResponseProps = {
  isSuccess: boolean;
  requiresTwoFactor: boolean;
  requiresEmailVerification: boolean;
  message: string;
  token: {
    accessToken: string;
    expiresIn: number;
    userInfo: {
      id: string;
      firstName: string;
      lastName: string;
      userName: string;
      organizationId: string;
      role: number;
      profileImage: string;
    };
  };
};
const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState<LoginRequestProps>({
    username: '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const { username, password } = state;
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<any>({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [flashbar, setFlashbar] = useState({
    isOpen: false,
    message: '',
    type: '',
  });
  const [toast, setToast] = useState({
    isOpen: false,
    message: '',
    type: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const validation = () => {
    const error: any = {};

    if (!username) {
      error.username = errorMessage.emptyUsername;
    }
    if (!password) {
      error.password = errorMessage.emptyPassword;
    }
    return error;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const format = /^\S*$/;
    format.test(event.target.value) &&
      setState({
        ...state,
        [event.target.name]: event.target.value,
      });

    if (event.target.value !== '') {
      setFormError({ ...formError, [event.target.name]: '' });
    } else {
      setFormError({
        ...formError,
        [event.target.name]: `Please enter ${event.target.name} `,
      });
    }
  };

  const handleClick = () => {
    setFormError(validation());
    setIsSubmit(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleClick();
    }
  };
  useEffect(() => {
    if (Object.keys(formError).length === 0 && isSubmit === true) {
      doLogin();
    }
  }, [formError]);

  const doLogin = async () => {
    setIsLoading(true);
    const obj = {
      username: username,
      password: password,
      rememberMe: rememberMe,
    };
    await axios
      .post(`${process.env.REACT_APP_API_BASEURL}/auth/login`, obj, {
        withCredentials: true,
      })
      .then((response) => {
        if (response.status === 200) {
          gotResponse(response.data);
        }
      })
      .catch((err) => {
        console.log(err);
        setFlashbar({
          ...flashbar,
          isOpen: true,
          type: flashBarTheme.ERROR,
          message: errorMessage.oops,
        });
        setIsLoading(false);
      });
  };

  const gotResponse = (data: LoginResponseProps) => {
    const {
      isSuccess,
      requiresTwoFactor,
      requiresEmailVerification,
      message,
      token,
    } = data;
    if (isSuccess && !requiresTwoFactor) {
      navigate('/dashboard');
      dispatch(updateTokenInfo(token));
      setIsLoading(false);
    }
    if (!isSuccess && requiresTwoFactor) {
      navigate(`/twofactorAuthorization/${rememberMe}`);
      setIsLoading(false);
    }
    if (!isSuccess && !requiresTwoFactor) {
      setFlashbar({
        ...flashbar,
        isOpen: true,
        type: flashBarTheme.ERROR,
        message: message,
      });
      setIsLoading(false);
    }
    if (!isSuccess && requiresEmailVerification) {
      navigate(`/checkmail/${btoa(username)}`);
    }
  };

  // For auto logout
  const autoLogout = useSelector((state: StateProps) => state.autoLogout);
  useEffect(() => {
    if (autoLogout) {
      setToast({ ...toast, isOpen: true });
      const timer = setTimeout(() => {
        setToast({ ...toast, isOpen: false });
        dispatch(updateAutoLogout(false));
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [autoLogout]);
  return (
    <div>
      {toast.isOpen && (
        <FlashBar
          message={errorMessage.sessionExpired}
          isOpen={true}
          type={flashBarTheme.ERROR}
          handleClose={() => {
            setToast({ ...toast, isOpen: false });
          }}
        />
      )}
      <FlashBar
        message={flashbar.message}
        isOpen={flashbar.isOpen}
        type={flashbar.type}
        handleClose={() => {
          setFlashbar({ ...flashbar, isOpen: false });
        }}
      />
      {isLoading ? <ActivityIndicator /> : null}
      <div className="relative flex justify-center items-center h-screen bg-slate-100">
        <form>
          <div className="border w-96 h-[400px] p-8 flex flex-col justify-center shadow-lg rounded-sm bg-white">
            <img src={logo} className="w-9/12 mx-auto pb-2" />

            <div className="mb-1">
              <div className={`font-semibold ${colors.primaryText} text-sm`}>
                Username
              </div>
              <div className="my-1 flex items-center">
                <div className="h-8 w-12 bg-[#173959] flex items-center justify-center ">
                  <i className="fa-solid fa-user text-white"></i>
                </div>

                <input
                  type="text"
                  name="username"
                  value={username}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(event);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="email"
                  className="h-8 focus:border-[#97dfdb] border block w-full text-sm placeholder-slate-300 text-slate-500 focus:outline-none pl-2 rounded-sm"
                  autoComplete="on"
                />
              </div>
              <div className="text-sm text-[red] font-semibold">
                {formError.username}
              </div>
            </div>
            <div className="mb-6">
              <div className={`font-semibold ${colors.primaryText} text-sm`}>
                Password
              </div>
              <div className="my-1 flex items-center">
                <div className="h-8 w-12 bg-[#173959] flex items-center justify-center py-1 rounded-l-sm">
                  <i className="fa-solid fa-key text-white"></i>
                </div>
                <div className="relative w-full">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChange(event);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="password"
                    className="h-8 focus:border-[#97dfdb] border block w-full text-sm placeholder-slate-300 text-slate-500 focus:outline-none pl-2 pr-7 rounded-sm"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 focus:outline-none p-0"
                    tabIndex={-1}
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <i className="fa-solid fa-eye"></i>
                    ) : (
                      <i className="fa-solid fa-eye-slash"></i>
                    )}
                  </button>
                </div>
              </div>
              <div className="text-sm text-[red] font-semibold">
                {formError.password}
              </div>
            </div>
            <button
              type="button"
              className="h-8 mt-3 bg-blue-500 rounded-sm flex items-center justify-center cursor-pointer "
              onClick={() => handleClick()}
            >
              <div className="text-white font-normal p-2">LOGIN</div>
            </button>
            <div className="flex justify-center">
              <Link
                className="mt-3 text-blue-500 cursor-pointer text-sm"
                to={'/forgotPassword'}
              >
                Forgot Password?
              </Link>
            </div>
          </div>
        </form>
        <div className="absolute right-0 bottom-0">
          {' '}
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Login;
